import { CalendarCheck } from 'lucide-react';
import type { AdminCompanyActivity } from 'app/javascript/types/admin';
import HealthPanel from 'app/javascript/components/admin/company_health/HealthPanel';
import HealthRow from 'app/javascript/components/admin/company_health/HealthRow';
import PhotoStatusBar from 'app/javascript/components/admin/company_health/PhotoStatusBar';
import { Separator } from 'app/javascript/components/ui/separator';
import { formatBrDate, formatBRDateTime } from 'app/javascript/utils/dates';

// "há 47 dias" is the number that matters for a quiet academy; the raw date
// alone makes the reader do the subtraction.
function lastCheckInLabel(activity: AdminCompanyActivity) {
  if (!activity.last_check_in_on) return 'nunca';

  const days = activity.days_since_last_check_in ?? 0;
  const date = formatBrDate(activity.last_check_in_on);
  if (days <= 0) return `hoje · ${date}`;
  if (days === 1) return `ontem · ${date}`;
  return `há ${days} dias · ${date}`;
}

interface ActivityPanelProps {
  activity: AdminCompanyActivity;
  className?: string;
}

export default function ActivityPanel({ activity, className }: ActivityPanelProps) {
  return (
    <HealthPanel
      className={className}
      icon={<CalendarCheck className="size-4" />}
      title="Atividade"
      action={
        <span className="text-xs text-muted-foreground">
          {activity.check_ins_last_30_days.toLocaleString('pt-BR')} check-ins em 30 dias
        </span>
      }
    >
      <div className="grid gap-x-8 sm:grid-cols-2">
        <div>
          <HealthRow label="Último check-in" value={lastCheckInLabel(activity)} />
          <HealthRow label="Check-ins hoje" value={activity.check_ins_today.toLocaleString('pt-BR')} />
          <HealthRow label="Check-ins no total" value={activity.check_ins_total.toLocaleString('pt-BR')} />
        </div>
        <div>
          <HealthRow label="Horários na grade" value={activity.class_schedules.toLocaleString('pt-BR')} />
          <HealthRow label="Aulas avulsas" value={activity.one_off_classes.toLocaleString('pt-BR')} />
          <HealthRow
            label="Última foto"
            value={activity.last_photo_at ? formatBRDateTime(activity.last_photo_at) : 'nunca'}
          />
        </div>
      </div>
      <Separator className="my-3" />
      <div className="flex items-baseline justify-between gap-4 pb-1">
        <span className="text-xs text-muted-foreground">Fotos de treino</span>
        <span className="text-sm font-medium tabular-nums">
          {activity.photos_total.toLocaleString('pt-BR')}
        </span>
      </div>
      <PhotoStatusBar counts={activity.photos_by_status} />
    </HealthPanel>
  );
}
